import { CheckCircle } from "@mui/icons-material";
import { dateTimeColumnSettings, ICity, IColumnsConfig, iconColumnSettings, setupColumnsConfig } from "@shared/library";
import { IGestSelectOption } from "@shared/library/dist/components/gest/GestInputs/select/GestSelectProps";
import GestListedTooltip from '@shared/library/dist/components/gest/GestListedTooltip/GestListedTooltip';
import GestProgressBar from '@shared/library/dist/components/gest/GestProgressBar/GestProgressBar';
import WorkerLogo from "../../../assets/images/icons/WorkerLogo.png";
import { IIntervention } from "./_models/Intervention";
import { IList } from "./_models/List";
import { IOperator } from "./_models/Operator";
import { IPlan } from "./_models/Plan";
import { composeName } from "./methods";

export const WFMControllerName = "WFM";
export const WFMLeftGrid = "calc(60vw - 124px)";
export const WFMRightGrid = "40vw";

//#region PLANS
interface IPlanColumnsConfig extends IColumnsConfig<IPlan> {
    operators?: IOperator[];
}

export const customDataPrefix = "custom_data_";
export const planHiddenColumns = { id: false, interventions: false };

export const planColumns = (columnsConfig?: IPlanColumnsConfig) =>
    setupColumnsConfig<IPlan>([
        //hiddden
        {
            field: 'id',
            filterable: false,
            hideable: false,
        },
        //visible
        iconColumnSettings(
            "operators"
            , params => params.row.operators?.length
                ? <img src={WorkerLogo} alt="Worker Icon" style={{ height: "20px" }} />
                : undefined
            , params => columnsConfig?.operators?.length && params.row.operators?.length
                ? <GestListedTooltip rows={columnsConfig?.operators.filter(o => params.row.operators!.includes(o.id as string)).map(o => o.username)} />
                : ""
        ),
        {
            field: 'progress',
            headerName: 'Progresso',
            width: 120,
            renderCell: params =>
                <GestProgressBar
                    style={{ height: "18px" }}
                    progressName={columnsConfig?.lang?.processed}
                    progress={params.row.processedCount ?? 0}
                    total={params.row.interventionsCount ?? 0}
                    redHighRange={20}
                />
        },
        {
            field: 'name',
            editable: true,
            width: 250,
        },
        {
            field: 'interventionsCount',
            headerName: columnsConfig?.lang?.interventions,
            align: "center",
            width: 70,
        },
        {
            field: 'processedCount',
            headerName: columnsConfig?.lang?.processed,
            align: "center",
            headerClassName: "text-success",
            cellClassName: "text-success",
            width: 70,
        },
        {
            field: "unprocessedCount",
            headerName: columnsConfig?.lang?.unprocessed,
            align: "center",
            headerClassName: "text-info",
            cellClassName: "text-info",
            width: 70,
        },
        {
            field: "attemptedCount",
            headerName: columnsConfig?.lang?.attempted_processing,
            align: "center",
            headerClassName: "text-danger",
            cellClassName: "text-danger",
            width: 70,
        },
        {
            field: "suspendedCount",
            headerName: columnsConfig?.lang?.suspended,
            align: "center",
            headerClassName: "text-warning",
            cellClassName: "text-warning",
            width: 70,
        },
        dateTimeColumnSettings("start_date", undefined, "DD/MM/YYYY HH:mm"),
        dateTimeColumnSettings("end_date", undefined, "DD/MM/YYYY HH:mm"),
        dateTimeColumnSettings("archived_at", undefined, "DD/MM/YYYY HH:mm"),
    ], columnsConfig);
//#endregion

//#region INTERVENTIONS
interface IInterventionColumnsConfig extends IColumnsConfig<IIntervention> {
    cities?: IGestSelectOption<ICity>[];
    plans?: IPlan[];
    lists?: IList[];
}

export const interventionColumns = (columnsConfig?: IInterventionColumnsConfig) =>
    setupColumnsConfig<IIntervention>([
        //#region hidden
        { field: "id", filterable: false, hideable: false, },
        { field: 'lat', filterable: false, hideable: false, },
        { field: 'lng', filterable: false, hideable: false, },
        {
            field: 'plan_id',
            headerName: columnsConfig?.lang?.plan,
            valueGetter: value => composeName(columnsConfig?.plans?.find(l => l.id === value)),
            renderCell: params => composeName(columnsConfig?.plans?.find(l => l.id === params.row.plan_id)),
        },
        {
            field: 'list_id',
            headerName: columnsConfig?.lang?.lists,
            valueGetter: value => composeName(columnsConfig?.lists?.find(l => l.id === value)),
            renderCell: params => composeName(columnsConfig?.lists?.find(l => l.id === params.value))
        },
        //#endregion
        {
            field: "intervention_id",//only for lists purpose
            headerName: columnsConfig?.lang?.plan,
            width: 150,
            renderCell: params => {
                const plan = columnsConfig?.plans?.find(p => p.id === params.row.plan_id)?.name;
                return plan
                    ? <strong className="align-items-center"><CheckCircle color='success' style={{ marginRight: 5 }} />{plan}</strong>
                    : undefined;
            }
        },
        {
            field: 'municipality',
            width: 150,
            valueGetter: (_, row) => columnsConfig?.cities?.find(a => a.value === row.municipality)?.label,
            renderCell: params => columnsConfig?.cities?.find(a => a.value === params.row.municipality)?.label
        },
        { field: 'district', },
        { field: 'address', width: 250 },
        { field: 'ciu', width: 150, align: "right", },
        {
            field: 'type',
            align: "center",
            width: 200,
            headerName: columnsConfig?.lang?.intervention_type,
            // valueGetter: value => columnsConfig?.lang?.[value],
            renderCell: params => columnsConfig?.lang?.[params.row.type?.toString() ?? ""] || params.row.type,
            cellClassName: params => params.row.type,
        },
        {
            field: 'status',
            width: 150,
            maxWidth: 150,
            valueGetter: value => columnsConfig?.lang?.[value ?? ""] || value,
            renderCell: params => columnsConfig?.lang?.[params.row.status?.toString() ?? ""] || params.row.status,
            cellClassName: params => params.row.status ?? "",
        },
        { field: 'odi', width: 200 },
        { field: 'supply_code', align: "right", width: 150 },
        { field: 'identity_code', align: "right", width: 150 },
        { field: 'supply_type', },
        { field: 'old_serial', },
        { field: 'new_serial', },
        { field: 'old_brand', },
        { field: 'new_brand', },
        { field: 'old_reading', type: 'number', },
        { field: 'new_reading', type: 'number', },
        { field: 'old_location', width: 200 },
        { field: 'new_location', width: 200 },
        { field: 'accountholder', width: 200 },
        { field: 'email', width: 200 },
        { field: 'telephone', },
        { field: 'causal', },
        { field: 'result', },
        { field: 'specifics', headerName: columnsConfig?.lang?.intervention_specifics, },
        { field: 'notes', },
        { field: 'annotations', },
        { field: 'anomaly', },
        { field: 'anomaly_notes', },
        { field: 'valves', headerName: "Valvola" },
        { field: 'diameter', type: 'number', },
        dateTimeColumnSettings("old_reading_date", undefined, "DD/MM/YYYY HH:mm"),
        dateTimeColumnSettings("intervention_date", undefined, "DD/MM/YYYY HH:mm"),
        dateTimeColumnSettings('intervention_start_date', undefined, "DD/MM/YYYY HH:mm"),
        dateTimeColumnSettings('intervention_end_date', undefined, "DD/MM/YYYY HH:mm"),
    ], columnsConfig);
//#endregion
